import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getIncidents,
  getAlertEvents,
  getTables,
  getTableFilter,
  getDatasetTags as getTags,
  getContracts,
  getTablesAccessed,
} from "@/helpers/apis";
import { toggleSetValue } from "../helpers/utils";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import usePaginate from "@/components/paginate/usePaginate";
import { useAppState } from "@/modules/app/useAppContext";
import type { GetTablesParams, TablesResponse } from "@/pages/datasets/types";
import { GetAlertEventsParams } from "@/pages/alerts/types";
import { PaginateProps } from "@/components/paginate";
import { GetIncidentsParams } from "@/pages/incidents/types";

const GET_CONTRACTS = "GET-CONTRACTS";
export const GET_INCIDENTS = "GET-INCIDENTS";
const GET_TABLE_FILTERS = "GET-TABLE-FILTERS";
const GET_TAGS = "GET-TAGS";
const GET_RECENT_TABLES = "GET-RECENT-TABLES";
export const GET_TABLES = "GET-TABLES";
export const GET_ALERT_NOTIFICATIONS = "GET-ALERT-NOTIFICATIONS";

interface FilterOption {
  label: string;
  value: string | string[];
}

interface UseMultiSelectReturn {
  getValue: (key: string | string[]) => boolean;
  toggleValue: (key: string) => void;
  reset: () => void;
  getTrueValues: () => string[];
  removeFilter: (removeLabel: string, contractOptions: FilterOption[]) => void;
}

interface UseContractFilterReturn {
  contractOptions: FilterOption[];
  contractFilter: UseMultiSelectReturn;
  isLoading: boolean;
}

export const useMultiSelect = (
  onChange: () => void,
  defaultValue: Map<string, boolean> = new Map(),
  searchParamKey?: string
): UseMultiSelectReturn => {
  const { searchParams, updateSearchParamsByKey, deleteSearchParamsByKeyValue } =
    useFiltersBySearchParams();
  const [map, setMap] = useState(defaultValue);
  const searchParamValue = searchParamKey
    ? searchParams.getAll(searchParamKey).join(",")
    : undefined;

  useEffect(() => {
    if (searchParamValue) {
      setMap(
        searchParamValue.split(",").reduce((acc, key) => {
          acc.set(key, true);
          return acc;
        }, new Map<string, boolean>())
      );
    } else {
      setMap(defaultValue);
    }
  }, [searchParamValue]);

  const updateMap = (mapValues: Map<string, boolean>): void => {
    if (searchParamKey) {
      if (mapValues.size === 0) {
        deleteSearchParamsByKeyValue({ [searchParamKey]: undefined });
        return;
      }
      updateSearchParamsByKey({
        [searchParamKey]: [...mapValues.keys()],
      });
    }
  };

  const getValue = (key: string | string[]): boolean => {
    if (Array.isArray(key)) return false;
    return !!map.get(key);
  };
  const toggleValue = (key: string): void => {
    onChange();
    setMap(_map => {
      const isUnchecked = Boolean(_map.get(key));

      const newMap = new Map(_map);
      if (isUnchecked) {
        newMap.delete(key);
      } else {
        newMap.set(key, !_map.get(key));
      }
      updateMap(newMap);
      return newMap;
    });
  };
  const reset = (): void => {
    onChange();
    setMap(new Map<string, boolean>());
    updateMap(new Map<string, boolean>());
  };
  const removeFilter = (removeLabel: string, contractOptions: FilterOption[]): void => {
    onChange();
    const removeKey = contractOptions.find(option => option.label === removeLabel)?.value;
    setMap(prevMap => {
      const newMap = new Map(prevMap);
      newMap.forEach((_, key) => {
        if (key === removeKey) {
          newMap.delete(key);
        }
      });
      updateMap(newMap);
      return newMap;
    });
  };

  const getTrueValues = (): string[] => [...map.entries()].filter(([, v]) => v).map(([k]) => k);

  return { getValue, toggleValue, reset, getTrueValues, removeFilter };
};

export const useSingleSelect = (
  onChange: () => void,
  isToggle = false,
  defaultValue = ""
): [string, (v: string) => void] => {
  const [value, setValue] = useState(defaultValue);
  return [
    value,
    (v: string) => {
      onChange();
      setValue(_v => (isToggle && v === _v ? defaultValue : v));
    },
  ];
};

export const useContractFilter = (onChange: () => void): UseContractFilterReturn => {
  const { data, isLoading } = useQuery({
    queryKey: [GET_CONTRACTS],
    queryFn: getContracts,
  });

  const contractFilter = useMultiSelect(onChange);
  const contractOptions = useMemo(() => {
    const contractMap: Record<string, string[]> = {};
    const contracts = data?.contracts || [];
    contracts.forEach(c => {
      if (!contractMap[c.name]) contractMap[c.name] = [];
      contractMap[c.name].push(c.id);
    });
    return Object.entries(contractMap).map(([k, v]) => ({
      label: k,
      value: v,
    }));
  }, [data]);

  return { contractOptions, contractFilter, isLoading };
};

const timeFilterLabels: Record<string, string> = { "24h": "24 hours", "1w": "1 week" };
const timeFilterOptions = ["24h", "1w"];

const levelFilterLabels: Record<string, string> = { error: "Error", warning: "Warning" };
const levelFilterOptions = ["error", "warning"];

const makeOptions = (values: string[], labels: Record<string, string>) =>
  values.map(v => ({ value: v, label: labels[v] }));

export const useIncidentFilter = ({
  tableId,
  eventId,
}: {
  tableId?: string | number;
  eventId?: string | number;
}) => {
  const [page, setPage] = useState(0);
  const resetPage = () => setPage(0);

  const {
    getValue: getLevelValue,
    toggleValue: toggleLevelValue,
    getTrueValues: getLevelFilter,
  } = useMultiSelect(resetPage);
  const { contractFilter, contractOptions } = useContractFilter(resetPage);

  const [timeFilter, setTimeFilter] = useSingleSelect(resetPage, true);

  const [sortAttribute, setSortAttribute] = useState("incident_id");
  const [sort, setSort] = useState<Record<string, string>>({
    incident_id: "",
    policy_name: "",
  });

  const levelFilter = getLevelFilter();
  const _contractFilter = contractFilter.getTrueValues();

  let { data: incidents, isLoading } = useQuery({
    queryKey: [
      GET_INCIDENTS,
      tableId,
      page + 1,
      sortAttribute,
      sort,
      timeFilter,
      eventId,
      ...levelFilter,
      ..._contractFilter,
    ],
    queryFn: () => {
      const params: GetIncidentsParams = { page: page + 1 };
      if (typeof tableId !== "undefined") params.table_id = tableId;
      if (levelFilter.length > 0) params.levels = levelFilter;
      if (timeFilter) params.timestamp = timeFilter;
      if (sortAttribute) params.sortColumn = sortAttribute;
      if (sort[sortAttribute]) params.sortOrder = sort[sortAttribute];
      if (_contractFilter.length > 0) params.contract_ids = _contractFilter;
      if (eventId) params.event_id = eventId;
      return getIncidents(params);
    },
  });

  const onSort = (column: string) => (key: string) => {
    setSort(prevSortOrder => ({
      ...prevSortOrder,
      [column]: key,
    }));
    setSortAttribute(column);
  };

  const isFilter = timeFilter || levelFilter.length > 0 || _contractFilter.length > 0;

  const paginate = {
    itemCount: incidents?.total ?? 0,
    page: page,
    pageSize: incidents?.size ?? 0,
    numPages: incidents?.pages ?? 0,
    onPageClick: setPage,
    onPageSizeChange: setPage,
    onResetPage: resetPage,
  } as PaginateProps;

  return {
    isLoading,
    incidents: incidents?.items,
    isFilter,
    paginate,

    sort: {
      onIncident: onSort("incident_id"),
      onPolicy: onSort("policy_name"),
      incidentSortValue: sort.incident_id,
      policySortValue: sort.policy_name,
    },

    timeFilter: {
      options: makeOptions(timeFilterOptions, timeFilterLabels),
      value: timeFilter,
      onChange: setTimeFilter,
      getLabel: () => timeFilterLabels[timeFilter] || "",
    },

    levelFilter: {
      options: makeOptions(levelFilterOptions, levelFilterLabels),
      value: getLevelValue,
      onChange: toggleLevelValue,
      getLabel: () => makeOptions(levelFilter, levelFilterLabels),
    },

    contractFilter,
    contractOptions,
  };
};

const defaultFilter = (): {
  datasources: Set<string>;
  databases: Set<string>;
  schemas: Set<string>;
} => ({
  datasources: new Set<string>(),
  databases: new Set<string>(),
  schemas: new Set<string>(),
});

interface DatasetFilterDefaults {
  tags?: string[];
}

export const useDatasetFilter = ({
  search,
  defaultFilters = {},
}: {
  search: string;
  defaultFilters?: DatasetFilterDefaults;
}) => {
  const { datastores } = useAppState();
  const { page, pageSize, setPage, updatePageSize, resetPage } = usePaginate();
  const { searchParams, updateSearchParamsByKey, deleteSearchParamsByKeyValue } =
    useFiltersBySearchParams();

  useEffect(resetPage, [search]);

  const {
    contractFilter,
    contractOptions,
    isLoading: isContractLoading,
  } = useContractFilter(resetPage);

  const filter: {
    datasources: Set<string>;
    databases: Set<string>;
    schemas: Set<string>;
  } = {
    datasources: new Set<string>(searchParams.getAll("datasources")),
    databases: new Set<string>(searchParams.getAll("databases")),
    schemas: new Set<string>(searchParams.getAll("schemas")),
  };

  const setFilter = (value: {
    datasources: Set<string>;
    databases: Set<string>;
    schemas: Set<string>;
  }) => {
    updateSearchParamsByKey(
      Object.entries(value).reduce(
        (acc, [key, value]) => {
          acc[key] = [...value];
          return acc;
        },
        {} as Record<string, string[]>
      )
    );
  };

  // default to snowflake
  const dsType = searchParams.get("datasource_type") || "snowflake";
  const setDSType = (value: string) => {
    resetPage();
    setFilter(defaultFilter());
    updateSearchParamsByKey({ datasource_type: value });
  };

  const { data: tableFilters, isFetching: isTableFiltersLoading } = useQuery({
    queryKey: [GET_TABLE_FILTERS],
    queryFn: getTableFilter,
  });

  const { data: tagOptions, isFetching: isTagOptionsLoading } = useQuery({
    queryKey: [GET_TAGS],
    queryFn: getTags,
  });

  const { data: recentlyAccessedTables, isFetching: isRecentlyAccessedTablesLoading } = useQuery({
    queryKey: [GET_RECENT_TABLES],
    queryFn: getTablesAccessed,
  });

  const { tags = [] } = defaultFilters;
  const _tagsDefaultFilterMap = new Map<string, boolean>();
  for (const t of tags) _tagsDefaultFilterMap.set(t, true);
  const tagFilter = useMultiSelect(() => {}, _tagsDefaultFilterMap, "insight");
  const _tagFilter = tagFilter.getTrueValues();

  const _contractFilter = contractFilter.getTrueValues();
  const [tables, setTables] = useState<TablesResponse | null>(null);
  const { data: tablesData, isFetching: isTableLoading } = useQuery({
    queryKey: [
      GET_TABLES,
      page + 1,
      pageSize,
      search,
      dsType,
      ..._contractFilter,
      ..._tagFilter,
      ...filter.datasources,
      ...filter.databases,
      ...filter.schemas,
    ],
    queryFn: () => {
      const params: GetTablesParams = { page: page + 1, size: pageSize };
      if (search) params.keyword = search;
      if (_contractFilter.length > 0) params.contract_ids = _contractFilter;
      if (_tagFilter.length > 0) params.tags = _tagFilter;
      if (dsType) params.datasource_type = dsType;
      const setParams = (key: "datasources" | "databases" | "schemas") => {
        if (filter[key].size > 0) params[key] = [...filter[key]];
      };
      setParams("datasources");
      setParams("databases");
      setParams("schemas");
      return getTables(params);
    },
  });

  // Handle successful tables data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (tablesData) {
      tablesData.items.forEach(t => {
        if (!t.tags) t.tags = [];
      });
      setTables(tablesData);
    }
  }, [tablesData]);

  const { datasources, databases, schemas } = useMemo(() => {
    const databases: { label: string; value: string; count: number; checked: boolean }[] = [];
    const schemas: { label: string; value: string; count: number; checked: boolean }[] = [];
    const datasources: { label: string; value: string; count: number; checked: boolean }[] = [];
    if (!dsType || !tableFilters) return { datasources, databases, schemas };
    const _tableFilters = tableFilters[dsType];
    for (let ds in _tableFilters) {
      const dsValue = _tableFilters[ds];
      const instanceName =
        (datastores ?? []).find(d => String(d.instance_id) === String(ds))?.name || ds;
      const dsItem = {
        label: instanceName,
        value: instanceName,
        count: Object.keys(dsValue).length,
        checked: filter.datasources.has(instanceName),
      };
      datasources.push(dsItem);
      if (dsItem.checked) {
        for (let db in dsValue) {
          const dbValue = dsValue[db];
          const dbItem = {
            label: db,
            value: db,
            count: Object.keys(dbValue).length,
            checked: filter.databases.has(db),
          };
          databases.push(dbItem);
          if (dbItem.checked) {
            for (let s in dbValue) {
              const sValue = dbValue[s];
              const sItem = {
                label: s,
                value: s,
                count: sValue,
                checked: filter.schemas.has(s),
              };
              schemas.push(sItem);
            }
          }
        }
      }
    }
    return { datasources, databases, schemas };
  }, [tableFilters, filter, dsType]);

  const isFilter =
    search ||
    filter.datasources.size > 0 ||
    filter.databases.size > 0 ||
    filter.schemas.size > 0 ||
    _contractFilter.length > 0 ||
    _tagFilter.length > 0;
  const isDataset = tables?.total === 0 && !isFilter;

  const onDatasourceChange = (x: string) => {
    setFilter({
      datasources: toggleSetValue(filter.datasources, x),
      databases: new Set(),
      schemas: new Set(),
    });
    resetPage();
  };
  const onDatabaseChange = (x: string) => {
    setFilter({
      datasources: filter.datasources,
      databases: toggleSetValue(filter.databases, x),
      schemas: new Set(),
    });
    resetPage();
  };
  const onSchemaChange = (x: string) => {
    setFilter({
      datasources: filter.datasources,
      databases: filter.databases,
      schemas: toggleSetValue(filter.schemas, x),
    });
    resetPage();
  };

  const resetFilter = () => {
    resetPage();
    contractFilter.reset();
    deleteSearchParamsByKeyValue({
      datasource_type: undefined,
      datasources: undefined,
      databases: undefined,
      schemas: undefined,
      insight: undefined,
    });
  };

  const paginate = {
    itemCount: tables?.total,
    page,
    pageSize,
    showPageSize: true,
    onPageSizeChange: updatePageSize,
    numPages: tables?.pages ?? 0,
    onPageClick: setPage,
  };

  const isLoading =
    isContractLoading ||
    isTableFiltersLoading ||
    isTagOptionsLoading ||
    isRecentlyAccessedTablesLoading;

  return {
    isDataset,
    tables: tables?.items,
    paginate,
    filter: {
      datasourceType: dsType,
      onDatasourceTypeChange: setDSType,
      datasources,
      onDatasourceChange,
      databases,
      onDatabaseChange,
      schemas,
      onSchemaChange,
      contractOptions: contractOptions.map(item => ({
        ...item,
        checked: contractFilter.getValue(item.value),
      })),
      onContractFilter: contractFilter.toggleValue,
      tagOptions: tagOptions?.column_badges?.map(item => ({
        label: item,
        value: item,
        checked: tagFilter.getValue(item),
      })),
      recentlyAccessedTables: recentlyAccessedTables,
      onTagChange: tagFilter.toggleValue,
      resetFilter,
    },
    isLoading,
    isTableLoading,
  };
};

export const useAlertNotificationsFilter = ({ alertId }: { alertId?: string | number }) => {
  const { page, pageSize, setPage, updatePageSize, resetPage } = usePaginate();
  const { searchParams } = useFiltersBySearchParams();

  const [timeFilter, setTimeFilter] = useSingleSelect(resetPage, true);

  const sortColumn = searchParams.get("sortColumn") || "alert_id";
  const sortOrder = searchParams.get("sortOrder") || "desc";

  let { data: notifications, isLoading } = useQuery({
    queryKey: [GET_ALERT_NOTIFICATIONS, alertId, page + 1, sortColumn, sortOrder, timeFilter],
    queryFn: () => {
      const params: GetAlertEventsParams = { page: page + 1, size: pageSize };
      if (typeof alertId !== "undefined") params.alert_id = alertId;
      if (timeFilter) params.timestamp = timeFilter;
      if (sortColumn) params.sortColumn = sortColumn;
      if (sortOrder) params.sortOrder = sortOrder;
      return getAlertEvents(params);
    },
  });

  const isFilter = timeFilter;

  const paginate = {
    itemCount: notifications?.total,
    page: page,
    pageSize: pageSize,
    numPages: notifications?.pages ?? 0,
    onPageClick: setPage,
    onPageSizeChange: updatePageSize,
    showPageSize: true,
  };

  return {
    isLoading,
    notifications: notifications?.items,
    isFilter,
    paginate,

    sortColumn,
    sortOrder,

    timeFilter: {
      options: makeOptions(timeFilterOptions, timeFilterLabels),
      value: timeFilter,
      onChange: setTimeFilter,
      getLabel: () => timeFilterLabels[timeFilter] || "",
    },
  };
};
